import type { TaxonomyNode } from '@/api/generatedApi';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { getApiError } from '@/types/apiError';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

export const TAXONOMY_CHILDREN_QUERY_KEY = 'taxonomy.children';

const PAGE_SIZE = 50;

export const useGetTaxonomyChildren = (
  taxonomyId: string | undefined,
  parentNodeId: string,
  options?: { enabled?: boolean }
) => {
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();

  const result = useInfiniteQuery({
    queryKey: [TAXONOMY_CHILDREN_QUERY_KEY, tenantId, taxonomyId, parentNodeId],
    queryFn: ({ pageParam }) =>
      api.taxonomy.listTaxonomyNodes({
        tenantId,
        taxonomyId: taxonomyId!,
        page: pageParam,
        perPage: PAGE_SIZE,
        parentNodeId,
      }),
    getNextPageParam: lastPage => {
      const { currentPage, lastPage: totalPages } = lastPage.data.pageData;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    initialPageParam: 1,
    enabled: Boolean(tenantId) && Boolean(taxonomyId) && options?.enabled !== false,
    retry: false,
  });

  const nodes: TaxonomyNode[] = useMemo(
    () => (result.data?.pages ?? []).flatMap(page => page.data.nodes),
    [result.data?.pages]
  );

  return {
    nodes,
    isLoading: result.isLoading,
    hasNextPage: result.hasNextPage ?? false,
    isFetchingNextPage: result.isFetchingNextPage,
    fetchNextPage: result.fetchNextPage,
    isSuccess: result.isSuccess,
    error: getApiError(result.error),
  };
};
